import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AREA_COLOURS, AREA_CORE_COUNT, areaColourName } from '../area-colours';
import { useAnchoredPopover } from './useAnchoredPopover';

/**
 * THE COLOUR CONTROL — a dropdown for choosing one of the identity palette's
 * colours, in place of the row of sixteen bare swatches.
 *
 * Sixteen loose squares in the form body read as untidy (Vinnie, 17 Oct). This is
 * the app's own field pattern instead: a trigger that shows the current colour and
 * its NAME with the same chevron the selects wear, opening the shared .sr-ta
 * popover as a NAMED two-column list — grouped "Core" and "Muted", all sixteen
 * visible at once.
 *
 * ANCHORED TO THE WINDOW, NOT THE GAP BY THE FIELD (Vinnie, 17 Oct — chose this
 * over scrolling or a denser grid). The full sixteen-colour list is ~450px tall;
 * measuring only the room directly below or above the trigger, it could not fit
 * low on a card or mid-page in Settings, so it used to cap and show a scrollbar.
 * Now it renders in a PORTAL at <body> and is positioned by hand against the whole
 * window (like CalendarPopover and a native <select> menu): below the trigger when
 * it fits, flipped above when it does not, always clamped fully inside the window.
 * So the page never jumps to make room AND the list never scrolls — it just lands
 * wherever it is wholly visible, floating over the field if it must. The portal
 * also escapes the Settings panel's own clip. A scroll is a last resort, only when
 * even the full window height cannot hold all sixteen (a very short window).
 *
 * Click-away and Escape are bound at document level (the portal sits outside the
 * field's DOM), so Escape closes the picker even in WebKit, where a <button> does
 * not take focus on a click — and stopPropagation keeps it from dropping the card.
 *
 * A stored colour that is NOT one of the sixteen (an area kept a retired hex, say)
 * never shows as a raw "#rrggbb": the trigger reads "Custom colour" over the real
 * swatch, and the value is pinned as a selected option at the top of the list so it
 * is kept and re-selectable — picking a new colour is a choice, never a silent loss.
 *
 * One control for every object that wears a palette colour: the area editor, the
 * label editor, and the epic picker when epics become objects. The options are
 * generated from AREA_COLOURS (the single hex+name source), so nothing drifts.
 */

interface Props {
  /** The chosen colour as a hex, or '' for "No colour" (only offered when allowNone). */
  value: string;
  onChange: (hex: string) => void;
  /** Offer a "No colour" option at the top — labels default to it; areas always have one. */
  allowNone?: boolean;
  disabled?: boolean;
  /** For the trigger's aria-label, e.g. the area or label name. */
  label?: string;
}

export default function ColourField({ value, onChange, allowNone = false, disabled = false, label }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  const named = value ? areaColourName(value) : undefined;
  // Every selectable value in render order — so the keyboard can rove the grid
  // the way Status/Area menus do (the colour picker was Tab-only; the other field
  // menus are arrow-driven — interaction witness, 17 Oct). "No colour" and a kept
  // off-palette "Custom" lead, then the Core then Muted swatches.
  const items = useMemo<string[]>(
    () => [
      ...(allowNone ? [''] : []),
      ...(value && !named ? [value] : []),
      ...AREA_COLOURS.map((c) => c.hex),
    ],
    [allowNone, value, named],
  );
  const activeVal = items[active] ?? '';
  const isActive = (hex: string) => hex.toLowerCase() === activeVal.toLowerCase();
  // Placed against the window, pinned to the trigger — the shared field-dropdown
  // rule (below, flip above, shift to fit, clamp inside, cap-to-window scroll only
  // as a last resort), so the colour menu behaves exactly like Area/Epic/Labels.
  const pos = useAnchoredPopover(open, trigger, pop);

  // Click-away and Escape — document level, the app-standard popover rule. The
  // popover is portaled outside the field, so a click inside it must be let
  // through (check both the trigger and the popover), or the option's own click
  // never fires. stopPropagation on Escape keeps it from dropping the card.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (trigger.current?.contains(t) || pop.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        trigger.current?.focus({ preventScroll: true });
      }
    };
    // Close when focus leaves the control (e.g. Tab-away) so the menu never
    // lingers open out of focus and swallows the next Escape (interaction witness,
    // 17 Oct). Deferred so a WebKit swatch click — which does not move focus —
    // is not read as leaving.
    const onFocusOut = () => {
      setTimeout(() => {
        const el = document.activeElement;
        if (trigger.current?.contains(el) || pop.current?.contains(el)) return;
        setOpen(false);
      }, 0);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  // Move focus into the list once the popover is on screen so the keyboard lands
  // somewhere — without scrolling the page to it (the window-jump fix; see the
  // focus-on-open lesson). It is rendered visibility:hidden until a position is
  // measured; focusing before that is silently dropped and leaves arrow-nav dead,
  // so gate on visibility, not just `open` (interaction witness, 17 Oct).
  const visible = open && !!pos;
  useEffect(() => {
    if (visible) pop.current?.focus({ preventScroll: true });
  }, [visible]);

  // Open on the chosen swatch so the keyboard starts there.
  useEffect(() => {
    if (!open) return;
    const i = items.findIndex((h) => h.toLowerCase() === value.toLowerCase());
    setActive(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted swatch in view as it moves.
  useEffect(() => {
    if (!visible) return;
    pop.current?.querySelector('.cp-opt.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active, visible]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  const pick = (hex: string) => {
    onChange(hex);
    close();
  };
  // Rove the swatches: left/right step one, up/down step a row (the grid is two
  // columns), Home/End jump to the ends, Enter/Space choose. Escape is caught by
  // the document listener above. (Interaction witness, 17 Oct — parity with the
  // other field menus' keyboard.)
  const move = (d: number) => setActive((a) => Math.min(Math.max(0, a + d), items.length - 1));

  const core = AREA_COLOURS.slice(0, AREA_CORE_COUNT);
  const muted = AREA_COLOURS.slice(AREA_CORE_COUNT);

  const option = (hex: string, name: string) => {
    const on = value.toLowerCase() === hex.toLowerCase();
    return (
      <button
        type="button"
        key={hex}
        className={`sr-ta-opt cp-opt ${on ? 'is-on' : ''} ${isActive(hex) ? 'is-active' : ''}`}
        aria-pressed={on}
        onMouseEnter={() => setActive(items.findIndex((h) => h.toLowerCase() === hex.toLowerCase()))}
        onClick={() => pick(hex)}
      >
        <span className="epic-sw cp-sw" style={{ background: hex }} aria-hidden="true" />
        <span className="sr-ta-opt-text"><span className="sr-ta-name">{name}</span></span>
        {on && (
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <span className="area-combo colour-combo" ref={wrap}>
      <button
        ref={trigger}
        type="button"
        className="ctl area-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label ? `Colour for ${label}` : 'Colour'}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {value ? (
          <>
            <span className="epic-sw area-trigger-sw" style={{ background: value }} aria-hidden="true" />
            <span className="area-trigger-name">{named ?? 'Custom colour'}</span>
          </>
        ) : (
          <>
            <span className="epic-sw epics-sw-none area-trigger-sw" aria-hidden="true" />
            <span className="area-trigger-name area-trigger-none">No colour</span>
          </>
        )}
      </button>

      {open && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop colour-pop"
          role="group"
          aria-label="Colour"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); move(2); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); move(-2); }
            else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
            else if (e.key === 'End') { e.preventDefault(); setActive(items.length - 1); }
            else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); pick(items[active]); }
          }}
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div className="sr-ta-body">
            {allowNone && (
              <button
                type="button"
                className={`sr-ta-opt cp-opt cp-none ${value ? '' : 'is-on'} ${isActive('') ? 'is-active' : ''}`}
                aria-pressed={!value}
                onMouseEnter={() => setActive(0)}
                onClick={() => pick('')}
              >
                <span className="epic-sw epics-sw-none cp-sw" aria-hidden="true" />
                <span className="sr-ta-opt-text"><span className="sr-ta-name">No colour</span></span>
                {!value && (
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            )}

            {value && !named && (
              <>
                <span className="sr-ta-label">Current</span>
                <div className="cp-grid">{option(value, 'Custom colour')}</div>
              </>
            )}

            <span className="sr-ta-label">Core</span>
            <div className="cp-grid">{core.map((c) => option(c.hex, c.name))}</div>

            <span className="sr-ta-label">Muted</span>
            <div className="cp-grid">{muted.map((c) => option(c.hex, c.name))}</div>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
